import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import { baseURL, resObj, sharepointCredentials } from "../utils/constants";

const msalConfig = {
  auth: {
    clientId: sharepointCredentials.clientId,
    authority: sharepointCredentials.authority,
    redirectUri: sharepointCredentials.redirectUri,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = { scopes: sharepointCredentials.scopes };

export const msalInstance = new PublicClientApplication(msalConfig);
const SSOContext = createContext();

export const SSOProvider = ({ children }) => {
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState(localStorage.getItem("accessToken") || null);
  const [account, setAccount] = useState(null);
  const [userInitials, setUserInitials] = useState('');
  const [msalReady, setMsalReady] = useState(false);
  const [apiRes, setApiRes] = useState(resObj);

  useEffect(() => {
    const initMSAL = async () => {
      try {
        await msalInstance.initialize();
        const currentAccount = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
        if (currentAccount) {
          msalInstance.setActiveAccount(currentAccount);
          setAccount(currentAccount);
        }
        setMsalReady(true);
      } catch (err) {
        setApiRes({
          ...apiRes,
          axiosError: true,
          errMsg: JSON.stringify(err),
          errHeading: "MSAL Initialization Error",
        });
      }
    };
    initMSAL();
  }, []);

  useEffect(() => {
    if (msalReady && !accessToken) acquireTokenSilently();
  }, [msalReady]);

  const acquireTokenSilently = async () => {
    try {
      const currentAccount = msalInstance.getActiveAccount();
      if (!currentAccount) return null;

      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: currentAccount,
      });

      const token = tokenResponse?.accessToken;
      localStorage.setItem("accessToken", token);
      setAccessToken(token);
      const decoded = jwtDecode(token);
      setUserInitials(`${decoded?.given_name[0]}${decoded?.family_name[0]}`.toUpperCase())
      return token;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        console.warn("Silent token failed, user interaction required.");
        return await login();
      } else {
        setApiRes({
          ...apiRes,
          axiosError: true,
          errMsg: JSON.stringify(err),
          errHeading: "Token acquisition error",
        });
      }
    }
  };

  const login = async () => {
    try {
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      const account = loginResponse.account;
      msalInstance.setActiveAccount(account);
      setAccount(account);

      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const token = tokenResponse.accessToken;
      localStorage.setItem("accessToken", token);
      setAccessToken(token);
      const decoded = jwtDecode(token);
      setUserInitials(`${decoded?.given_name[0]}${decoded?.family_name[0]}`.toUpperCase())
      navigate("/");
    } catch (error) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(error.errorMessage),
        errHeading: "SSO Login Error",
      });
    }
  };

  const logout = async () => {
    await msalInstance.logoutPopup();
    localStorage.removeItem("accessToken");
    setAccessToken(null);
    setAccount(null);
    navigate("/logout");
  };

  const getSSOProtectedData = async (params = {}) => {
    try {
      if (!msalReady) await new Promise((r) => setTimeout(r, 200));
      let token = await acquireTokenSilently();
      if (!token) throw new Error("No access token available");

      await axios.get(`${baseURL}/api/protected`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        navigate("/logout");
      } else {
        // navigate("/error");
        navigate("/logout");
      }
      // setApiRes({
      //   loading: false,
      //   axiosError: true,
      //   errMsg: JSON.stringify(err.response?.data || err.message).replace(
      //     /[{}[\]"']/g, ""),
      //   errHeading: "SSO API Call",
      // });
    }
  };

  return (
    <SSOContext.Provider
      value={{
        apiRes, setApiRes, login, logout, getSSOProtectedData,
        accessToken, account, acquireTokenSilently, userInitials,
      }}
    >
      {children}
    </SSOContext.Provider>
  );
};

export const useSSO = () => useContext(SSOContext);